import Axios from 'axios'; // central interceptor, leave as is
import { timeoutMessage } from '../utils/timeoutMessage';

// glb importu (global değişkenler için)
import glb from '../globals';

// Aynı istek 500 ms içinde tekrar gelirse tekrar gönderme, önceki yanıtı kullan (tekrarlama önleme)
const DEDUPE_MS = 500;
const recentByKey = new Map();
const inFlightByKey = new Map();

function formatLocalDateTime(dt) {
  const d = dt instanceof Date ? dt : new Date(dt);
  if (Number.isNaN(d.getTime())) return dt;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function isIsoWithTimezone(v) {
  if (typeof v !== 'string') return false;
  // Examples: 2026-03-26T10:00:00Z, 2026-03-26T10:00Z, 2026-03-26T10:00:00+03:00
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+\-]\d{2}:?\d{2})$/.test(v);
}

function normalizeDatesForTransport(value) {
  if (value == null) return value;
  if (value instanceof Date) return formatLocalDateTime(value);
  if (Array.isArray(value)) return value.map(normalizeDatesForTransport);
  if (typeof FormData !== 'undefined' && value instanceof FormData) return value;
  if (typeof value === 'object') {
    const out = {};
    Object.keys(value).forEach((k) => {
      out[k] = normalizeDatesForTransport(value[k]);
    });
    return out;
  }
  if (isIsoWithTimezone(value)) return formatLocalDateTime(value);
  return value;
}

function requestKey(config) {
  try {
    // Binary payloads cannot be compared with JSON.stringify (all become {}).
    if ((typeof FormData !== 'undefined' && config.data instanceof FormData)
      || (typeof Blob !== 'undefined' && config.data instanceof Blob)
      || config.data instanceof ArrayBuffer || ArrayBuffer.isView(config.data)) return '';
    const method = (config.method || 'get').toLowerCase();

    // URL + baseURL kombinasyonunu normalize et (sadece path + query önemli)
    const rawUrl = typeof config.url === 'string'
      ? config.url
      : (config.baseURL || '') + (config.url || '');

    let urlObj;
    try {
      urlObj = new URL(rawUrl, window.location?.origin || 'http://localhost');
    } catch {
      // new URL hata verirse, en azından path'i string olarak kullan
      urlObj = { pathname: rawUrl, searchParams: new URLSearchParams() };
    }

    const path = urlObj.pathname || '';

    // URL içindeki query + config.params birleşik ve sıralı olsun
    const mergedParams = {};

    // 1) URL üzerindeki mevcut query'ler
    if (urlObj.searchParams && typeof urlObj.searchParams.forEach === 'function') {
      urlObj.searchParams.forEach((value, key) => {
        mergedParams[key] = value;
      });
    }

    // 2) config.params (aynı key varsa URL'dekini ezsin)
    if (config.params != null) {
      if (typeof config.params === 'object') {
        Object.keys(config.params).forEach((k) => {
          const v = config.params[k];
          if (v !== undefined && v !== null) {
            mergedParams[k] = String(v);
          }
        });
      } else {
        // Obje değilse, ham string olarak ekle (nadir durum)
        mergedParams.__raw = String(config.params);
      }
    }

    // Her istekte değişen (cache-buster) parametreleri yok say (aynı istek algısını bozmasın)
    const noiseKeys = ['_', 'rnd', 'ts', 'timestamp', 'cacheBust', 'cb'];
    noiseKeys.forEach((k) => {
      if (k in mergedParams) {
        delete mergedParams[k];
      }
    });

    const paramKeys = Object.keys(mergedParams).sort();
    const p = new URLSearchParams();
    paramKeys.forEach((k) => {
      const v = mergedParams[k];
      if (v !== undefined && v !== null) p.append(k, v);
    });
    const params = p.toString();

    const data = config.data != null
      ? (typeof config.data === 'object' ? JSON.stringify(config.data) : String(config.data))
      : '';

    // method | path?sortedParams | body
    return `${method}|${path}${params ? '?' + params : ''}|${data}`;
  } catch (e) {
    return '';
  }
}

// Axios 1.x stores adapter names in an array rather than a callable function.
const defaultAdapter = typeof Axios.defaults.adapter === 'function'
  ? Axios.defaults.adapter
  : Axios.getAdapter(Axios.defaults.adapter);
if (typeof defaultAdapter === 'function') {
  Axios.defaults.adapter = function (config) {
    const isAuthLogin = typeof config.url === 'string' && /\/authusr(\?|$)/i.test(config.url)
      && String(config.method || 'get').toLowerCase() === 'get'
      && !config.url.includes('/authusr/me');
    const key = isAuthLogin ? '' : requestKey(config);
    const now = Date.now();
    if (key) {
      const recent = recentByKey.get(key);
      if (recent && (now - recent.ts) < DEDUPE_MS) {
        return Promise.resolve(recent.response);
      }
      const existing = inFlightByKey.get(key);
      if (existing) {
        return existing;
      }
    }
    const promise = defaultAdapter(config)
      .then((response) => {
        if (key && response.status < 400) {
          recentByKey.set(key, { response, ts: Date.now() });
          setTimeout(() => {
            if (recentByKey.get(key)?.response === response) recentByKey.delete(key);
          }, DEDUPE_MS);
          inFlightByKey.delete(key);
        } else if (key) {
          inFlightByKey.delete(key);
        }
        return response;
      })
      .catch((err) => {
        if (key) inFlightByKey.delete(key);
        throw err;
      });
    if (key) inFlightByKey.set(key, promise);
    return promise;
  };
}

// Tüm isteklerde otomatik olarak JWT token ekle
Axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('jwt_token');
    if (token) {
      config.headers['Authorization'] = 'Bearer ' + token;
    }
    
    // Login request'ine cid ve kulid ekleme (ama token'ı ekle)
    if (config.url && config.url.includes('/authusr')) {
      return config;
    }

    // Date -> local datetime string (timezone kaymasını engelle)
    if (config.data) {
      config.data = normalizeDatesForTransport(config.data);
    }
    if (config.params && typeof config.params === 'object') {
      config.params = normalizeDatesForTransport(config.params);
    }
    
    // cid ve kulid'i query string'e ekle (config.params'ta zaten varsa tekrarlama)
    try {
      const g = glb?.default ?? glb;
      const cid = g?.cid || g?.CID || g?.KULLANICI?.KUL_DEF_FIR_REF || g?.KULLANICI?.KUL_FIR_REF;
      const kulid = g?.kulid || g?.KULID || g?.KULLANICI?.kulid || g?.KULLANICI?.KUL_REF;
      const paramsObj = (config.params && typeof config.params === 'object' && !Array.isArray(config.params))
        ? config.params
        : null;

      if (cid || kulid) {
        let url = config.url;
        const urlObj = new URL(url, window.location.origin);
        const hasCid = urlObj.searchParams.has('cid') || (paramsObj && Object.prototype.hasOwnProperty.call(paramsObj, 'cid'));
        const hasKulid = urlObj.searchParams.has('kulid') || (paramsObj && Object.prototype.hasOwnProperty.call(paramsObj, 'kulid'));
        if (cid && !hasCid) urlObj.searchParams.append('cid', cid);
        if (kulid && !hasKulid) urlObj.searchParams.append('kulid', kulid);
        config.url = urlObj.toString();
      }
    } catch (e) { /* ignore */ }
    return config;
  },
  (error) => Promise.reject(error)
);

Axios.interceptors.response.use(
  response => response,
  error => {
    const isTimeout = Number(error?.response?.status) === 504
      || error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT';
    if (isTimeout) {
      const config = error.config || error.response?.config || {};
      const path = String(config.url || '').split(/[?#]/)[0];
      const isReport = /(?:^|\/)(?:report|rapor)(?:\/|$)/i.test(path);
      const message = timeoutMessage(isReport);
      error.message = message;
      if (error.response) {
        const data = error.response.data;
        error.response.statusText = 'Süre aşımı';
        error.response.data = {
          ...(data && Object.getPrototypeOf(data) === Object.prototype ? data : {}),
          message,
          error: message,
          errmsg: message,
        };
      }
    }
    return Promise.reject(error);
  }
);

export default Axios;
